import React from 'react';
import {Button, FlexColumn, FlexRow, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {ReportDownloadMenu} from './ReportDownloadMenu';
import {ReportShareButton} from './ReportShareButton';
import {ReportPeriodControls} from './ReportPeriodControls';

const compTokens = tokensForThemeProvider.sky.comp;

/** The line under the toolbar naming who holds the edit lock. Degrades rather than disappearing:
 * the fact that matters to a blocked editor is that somebody has the report open, and the name and
 * the time are detail on top of that. A blank space beside a disabled button explains nothing.
 *
 * A holder with no resolvable name yields `fullName === ''`, never null, so the test is non-empty
 * rather than present. An unparseable timestamp is dropped rather than formatted, because
 * `formatDate` renders one as the literal string "Invalid Date". */
function LockHolderLine(): JSX.Element | null {
	const {formatMessage, formatDate} = useIntl();
	const {role, lockState, lockHolder, lockedAt} = useReportBuilder();

	if (role !== 'editor' || lockState !== 'blocked') {
		return null;
	}

	const name = lockHolder?.fullName ?? '';
	const hasName = name.length > 0;
	const hasTime = lockedAt !== null && !Number.isNaN(Date.parse(lockedAt));
	const time = hasTime ? formatDate(lockedAt as string, {hour: 'numeric', minute: 'numeric'}) : '';

	const message = hasName
		? hasTime
			? formatMessage({id: 'ai_report_builder.locked_by_since'}, {name, time})
			: formatMessage({id: 'ai_report_builder.locked_by'}, {name})
		: hasTime
		? formatMessage({id: 'ai_report_builder.locked_since_anon'}, {time})
		: formatMessage({id: 'ai_report_builder.locked_anon'});

	return (
		<Text size={'2'} color={compTokens.alerts.color.text.information} data-cy={'ai-report-lock-holder-line'}>
			{message}
		</Text>
	);
}

/** Top bar shown in view mode. The reporting period sits on the left, read-only: it is part of what
 * the numbers on screen mean, so a reader needs to see it, but a reader must not be able to change it
 * — see `ReportPeriodControls` for why writing from here would be a defect rather than a feature.
 * Editors additionally see "Share" and "Edit Report" (opens the edit session via `beginEdit()`, which
 * switches `mode` to `'edit'` once the draft is opened); readers only ever see "Download". Download
 * exports the report. Share opens the report-sharing dialog once the report is saved; on an unsaved
 * draft it stays disabled with a tooltip explaining why, since a draft has no Reports-list entry to
 * share. Left-to-right order in the action group is Download, Share, Edit Report, matching the design
 * — the reverse of DOM/visual emphasis order used previously. Download and Share are monochrome
 * (grey-bordered) like every secondary action in both bars; only the one primary action per bar
 * carries the filled high-emphasis treatment.
 *
 * A blocked Edit Report button carries no tooltip: `LockHolderLine`, rendered directly beneath the
 * row, says why permanently, and a hover-only explanation is worse than a visible one. */
export function ViewTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {role, beginEdit, lockState} = useReportBuilder();

	return (
		<FlexColumn alignItems={'stretch'} gap={'xs'} data-cy={'ai-report-view-top-bar-column'}>
			<FlexRow justifyContent={'space-between'} alignItems={'center'} gap={'s'} data-cy={'ai-report-view-top-bar'}>
				<ReportPeriodControls readOnly />
				{/* DS FlexRow defaults to flex:1, so this group would take half the bar and drift inward;
				 * pinned to its content so space-between pushes it to the true right edge, matching EditTopBar. */}
				<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
					<ReportDownloadMenu />
					<ReportShareButton />
					{role === 'editor' ? (
						<Button
							emphasis={'high'}
							icon={'edit'}
							disabled={lockState === 'blocked'}
							onPress={lockState === 'blocked' ? undefined : () => void beginEdit()}
							data-cy={'ai-report-edit-report-button'}
						>
							{formatMessage({id: 'ai_report_builder.edit_report'})}
						</Button>
					) : null}
				</FlexRow>
			</FlexRow>
			{/* Beneath the whole bar and right-aligned under the action group it explains. The column stretches
			 * so the row above can still space-between the period controls against those actions. */}
			<FlexRow justifyContent={'flex-end'}>
				<LockHolderLine />
			</FlexRow>
		</FlexColumn>
	);
}
